import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Bildmarke, Schriftzug } from '../components/Marke';
import { IconHaken, IconObjekte, IconMieter } from '../components/icons';
import { cn } from '../lib/cn';
import { transition, uebergangSanft } from '../lib/motion';
import {
  DEMO,
  ApiFehler,
  type Benutzer,
  type Mandant,
  onboardingAbschliessen,
  passwortAendern,
} from '../lib/api';

/**
 * Einrichtung nach der ersten Anmeldung (Bauplan „Registrierung & Login“, Abschnitt 8):
 *   1. Zugänge mit Passwort-Änderungspflicht (vom Verwalter angelegt) legen ein eigenes Passwort fest.
 *   2. Neue Konten wählen die Art der Nutzung und optional den Firmennamen.
 */

const PASSWORT_MIN = 8;

type Typ = 'privatvermieter' | 'verwaltung';

const TYPEN: Array<{ wert: Typ; titel: string; text: string; icon: ReactNode }> = [
  {
    wert: 'privatvermieter',
    titel: 'Privatvermieter',
    text: 'Eigene Wohnungen oder Häuser verwalten und abrechnen.',
    icon: <IconMieter groesse={22} />,
  },
  {
    wert: 'verwaltung',
    titel: 'Hausverwaltung',
    text: 'Objekte im Auftrag von Eigentümern betreuen.',
    icon: <IconObjekte groesse={22} />,
  },
];

export interface KontoEinrichtungProps {
  benutzer: Benutzer;
  mandant: Mandant;
  onFertig: () => void;
  onAbmelden: () => void;
}

export function KontoEinrichtung({ benutzer, mandant, onFertig, onAbmelden }: KontoEinrichtungProps) {
  const reduziert = useReducedMotion();
  const [schritt, setSchritt] = useState<'passwort' | 'art'>(benutzer.passwortAendern ? 'passwort' : 'art');

  function nachPasswort() {
    if (benutzer.onboardingErforderlich) setSchritt('art');
    else onFertig();
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-bg px-gutter py-6">
      <div className="w-full max-w-md">
        <div className="mb-5 flex items-center gap-3">
          <Bildmarke groesse={48} puls="dezent" className="text-fg" titel="GrundPuls" />
          <p className="text-2xl leading-tight">
            <Schriftzug />
          </p>
        </div>

        <motion.div
          layout
          transition={transition(!!reduziert, { type: 'spring', stiffness: 260, damping: 32 })}
          className="rounded-xl border border-border bg-surface p-5 shadow-md"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={schritt}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={transition(!!reduziert, uebergangSanft)}
            >
              {schritt === 'passwort' ? (
                <PasswortPflicht benutzer={benutzer} onFertig={nachPasswort} />
              ) : (
                <ArtWaehlen benutzer={benutzer} mandant={mandant} onFertig={onFertig} />
              )}
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <button
          type="button"
          onClick={onAbmelden}
          className="mx-auto mt-4 block py-1 text-sm font-medium text-fg-muted hover:text-fg hover:underline"
        >
          Abmelden
        </button>
      </div>
    </main>
  );
}

function ArtWaehlen({ benutzer, mandant, onFertig }: { benutzer: Benutzer; mandant: Mandant; onFertig: () => void }) {
  const istInhaber = benutzer.rolle === 'admin';
  const [typ, setTyp] = useState<Typ | null>(null);
  const [firma, setFirma] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    if (istInhaber && !typ) {
      setFehler('Bitte wählen Sie die Art der Nutzung.');
      return;
    }
    setFehler(null);
    setLaeuft(true);
    try {
      if (!DEMO) await onboardingAbschliessen(typ ?? 'privatvermieter', firma.trim() || undefined);
      onFertig();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Einrichtung konnte nicht gespeichert werden.');
      setLaeuft(false);
    }
  }

  const testphase = mandant.testphaseBis ? datum(mandant.testphaseBis) : null;

  return (
    <form className="space-y-4" onSubmit={absenden} noValidate>
      <div>
        <h1 className="text-lg font-semibold text-fg">
          {benutzer.vorname ? `Willkommen, ${benutzer.vorname}` : 'Willkommen'}
        </h1>
        <p className="mt-1 text-sm leading-relaxed text-fg-muted">
          {istInhaber
            ? 'Zwei Angaben, dann ist Ihr Konto eingerichtet.'
            : 'Ihr Zugang ist eingerichtet.'}
        </p>
      </div>

      {testphase && (
        <p className="flex items-center gap-2 rounded-lg bg-accent-soft px-3 py-2 text-sm text-accent-soft-fg">
          <IconHaken groesse={16} /> Testphase bis {testphase}, ohne Zahlungsangaben.
        </p>
      )}

      {istInhaber && (
        <>
          <div role="radiogroup" aria-label="Art der Nutzung" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {TYPEN.map((t) => {
              const aktiv = typ === t.wert;
              return (
                <button
                  key={t.wert}
                  type="button"
                  role="radio"
                  aria-checked={aktiv}
                  onClick={() => {
                    setTyp(t.wert);
                    setFehler(null);
                  }}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors duration-fast ease-out',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
                    aktiv ? 'border-accent bg-accent-soft' : 'border-border-strong hover:bg-surface-2',
                  )}
                >
                  <span className={cn('mt-0.5 shrink-0', aktiv ? 'text-accent' : 'text-fg-muted')}>{t.icon}</span>
                  <span className="min-w-0">
                    <span className={cn('block text-sm font-semibold', aktiv ? 'text-accent-soft-fg' : 'text-fg')}>
                      {t.titel}
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-fg-muted">{t.text}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <Input
            label={typ === 'verwaltung' ? 'Name der Hausverwaltung (optional)' : 'Firma (optional)'}
            hinweis="Erscheint als Absender auf Abrechnungen. Später in den Einstellungen änderbar."
            autoComplete="organization"
            value={firma}
            onChange={(e) => setFirma(e.target.value)}
          />
        </>
      )}

      {fehler && (
        <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-soft-fg">
          {fehler}
        </p>
      )}

      <Button type="submit" volleBreite groesse="lg" ladend={laeuft}>
        {istInhaber ? 'Einrichtung abschließen' : 'Weiter'}
      </Button>
    </form>
  );
}

function PasswortPflicht({ benutzer, onFertig }: { benutzer: Benutzer; onFertig: () => void }) {
  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [neu2, setNeu2] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    if (neu.length < PASSWORT_MIN) return setFehler(`Das Passwort muss mindestens ${PASSWORT_MIN} Zeichen lang sein.`);
    if (neu !== neu2) return setFehler('Die Passwörter stimmen nicht überein.');
    setFehler(null);
    setLaeuft(true);
    try {
      if (!DEMO) await passwortAendern(benutzer.passwortGesetzt ? alt : undefined, neu);
      onFertig();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Das Passwort konnte nicht gespeichert werden.');
      setLaeuft(false);
    }
  }

  return (
    <form className="space-y-4" onSubmit={absenden} noValidate>
      <div>
        <h1 className="text-lg font-semibold text-fg">Eigenes Passwort festlegen</h1>
        <p className="mt-1 text-sm leading-relaxed text-fg-muted">
          Ihr Zugang wurde mit einem vorläufigen Passwort angelegt. Bitte legen Sie vor der ersten Nutzung ein
          eigenes Passwort fest.
        </p>
      </div>
      {benutzer.passwortGesetzt && (
        <Input
          label="Vorläufiges Passwort"
          type="password"
          autoComplete="current-password"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          autoFocus
        />
      )}
      <Input
        label="Neues Passwort"
        type="password"
        autoComplete="new-password"
        hinweis={`Mindestens ${PASSWORT_MIN} Zeichen.`}
        value={neu}
        onChange={(e) => setNeu(e.target.value)}
      />
      <Input
        label="Neues Passwort wiederholen"
        type="password"
        autoComplete="new-password"
        value={neu2}
        onChange={(e) => setNeu2(e.target.value)}
      />
      {fehler && (
        <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-soft-fg">
          {fehler}
        </p>
      )}
      <Button type="submit" volleBreite groesse="lg" ladend={laeuft}>
        Passwort speichern
      </Button>
    </form>
  );
}

function datum(iso: string): string {
  const [j, m, t] = iso.slice(0, 10).split('-');
  return j && m && t ? `${t}.${m}.${j}` : iso;
}

export default KontoEinrichtung;
